/**
 * Conversion Funnel Visualization
 *
 * Shows the user journey from visit to conversion
 * with drop-off rates at each stage
 */

import { TrendingDown } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
  color: string;
}

interface ConversionFunnelProps {
  totalSessions: number;
  pageViews: number;
  toolInteractions: number;
  conversions: number;
}

export function ConversionFunnel({
  totalSessions,
  pageViews,
  toolInteractions,
  conversions,
}: ConversionFunnelProps) {
  // Calculate funnel data
  const funnelData: FunnelStage[] = [
    {
      stage: "Sessions",
      count: totalSessions,
      percentage: 100,
      color: "#94a3b8", // slate-400
    },
    {
      stage: "Page Views",
      count: pageViews,
      percentage: totalSessions > 0 ? (pageViews / totalSessions) * 100 : 0,
      color: "#64748b", // slate-500
    },
    {
      stage: "Tool Usage",
      count: toolInteractions,
      percentage:
        totalSessions > 0 ? (toolInteractions / totalSessions) * 100 : 0,
      color: "#B87333", // brand color
    },
    {
      stage: "Conversions",
      count: conversions,
      percentage: totalSessions > 0 ? (conversions / totalSessions) * 100 : 0,
      color: "#10b981", // green-500
    },
  ];

  // Calculate drop-off rates
  const dropOffRates = funnelData
    .map((stage, index) => {
      if (index === 0) return null;
      const previousStage = funnelData[index - 1];
      const dropOff =
        previousStage.count > 0
          ? ((previousStage.count - stage.count) / previousStage.count) * 100
          : 0;
      return {
        stage: stage.stage,
        dropOff: Math.round(dropOff * 10) / 10,
      };
    })
    .filter(Boolean);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload?.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border-2 border-slate-200">
          <p className="font-bold text-slate-900 mb-2">{data.stage}</p>
          <p className="text-sm text-slate-600 mb-1">
            Count:{" "}
            <span className="font-semibold text-[#B87333]">
              {data.count.toLocaleString()}
            </span>
          </p>
          <p className="text-sm text-slate-600">
            Retention:{" "}
            <span className="font-semibold text-green-600">
              {data.percentage.toFixed(1)}%
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Funnel Chart */}
      <div className="bg-white p-6 rounded-lg">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={funnelData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="stage" type="category" width={120} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[0, 8, 8, 0]}>
              {funnelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Drop-off Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {dropOffRates.map((item: any, index) => (
          <div
            key={index}
            className="bg-slate-50 p-4 rounded-lg border-2 border-slate-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="h-4 w-4 text-red-500" />
              <p className="text-sm font-semibold text-slate-700">
                Drop-off to {item.stage}
              </p>
            </div>
            <p className="text-2xl font-bold text-red-600">{item.dropOff}%</p>
          </div>
        ))}
      </div>

      {/* Funnel Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {funnelData.map((stage, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded-lg border-2 border-slate-200 hover:border-[#B87333] transition-colors"
          >
            <div
              className="w-3 h-3 rounded-full mb-2"
              style={{ backgroundColor: stage.color }}
            />
            <p className="text-xs text-slate-600 mb-1">{stage.stage}</p>
            <p className="text-xl font-bold text-slate-900 mb-1">
              {stage.count.toLocaleString()}
            </p>
            <p className="text-xs text-slate-500">
              {stage.percentage.toFixed(1)}% retention
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
